import { RightsholderContacts } from '@Entities/rightsholder/rightsholder-contact'
import { getPublicContacts } from '@App/api/routes/rightsholder/contact'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { PublisherModel } from '@Entities/work/publisher-model'
import { TableRowData } from '@Components/ViewWork/Table'
import { tableCellSanitizer } from '@App/helpers/table-cell-sanitizer'
import CellInput from '@Components/ViewWork/Table/ValidationCells/CellInput'
import CellDisplayList from '@Components/ViewWork/Table/ValidationCells/CellDisplayList'
import ContactDetailsBlock from '@Components/ViewWork/PublisherCard/ContactDetailsBlock'
import React from 'react'

export const setupPublicContacts = async (ipids: number[]): Promise<RightsholderContacts | undefined> => {
    if (ipids?.length) {
        const response = await getPublicContacts(ipids)
        return response.data ?? undefined
    }
    return undefined
}

export const getPublisherTableRows = (
    rows: Array<{ publisherModel: OriginalPublisherModel; adminModel: PublisherModel | undefined }>,
    contacts: RightsholderContacts | undefined,
    updateShare: (model: OriginalPublisherModel, newValue: number, adminModel?: PublisherModel) => void,
    currentRightsholderIpId: number,
    isAdminRows: boolean,
    allowShareEditing?: boolean
) => {
    const publishersType = isAdminRows ? 'administrator' : 'publisher'
    return rows.map<TableRowData>(({ publisherModel, adminModel }, index) => {
        const targetPublisher = adminModel ? adminModel : publisherModel
        return {
            name: `${targetPublisher.ipId}`,
            headerRow: { row: [] },
            removeCellIdentifier: tableCellSanitizer(targetPublisher.ipId),
            removable: true,
            cells: [
                <CellInput
                    defaultValue={targetPublisher.publisherName}
                    readOnly
                    name={`rows.${publishersType}Rows[${index}].${publishersType}Name`}
                    key={`rows.${publishersType}Rows[${index}].${publishersType}Name`}
                    type="text"
                />,
                <CellInput
                    name={`rows.${publishersType}Rows[${index}].${publishersType}PNumber`}
                    key={`rows.${publishersType}Rows[${index}].${publishersType}PNumber`}
                    defaultValue={targetPublisher.hfaPublisherNumber ?? ''}
                    readOnly
                    type="text"
                />,
                <CellInput
                    name={`rows.${publishersType}Rows[${index}].${publishersType}IPINumber`}
                    key={`rows.${publishersType}Rows[${index}].${publishersType}IPINumber`}
                    defaultValue={tableCellSanitizer(targetPublisher.ipiNumber)}
                    readOnly
                    type="number"
                />,
                <CellDisplayList
                    key={`rows.${publishersType}Rows[${index}].writersList`}
                    items={publisherModel.writers}
                />,
                <CellInput
                    name={`rows.${publishersType}Rows[${index}].${publishersType}Share`}
                    key={`rows.${publishersType}Rows[${index}].${publishersType}Share`}
                    defaultValue={targetPublisher.publisherShare ?? ''}
                    min={0}
                    max={100.06}
                    readOnly={!allowShareEditing || !(Number(publisherModel.topMemberIpId) === currentRightsholderIpId)}
                    onValueChanged={(value) => {
                        if (isAdminRows) {
                            updateShare(publisherModel, value as number, adminModel)
                        } else if (publisherModel.administratorPublishers.length === 0) {
                            updateShare(publisherModel, value as number)
                        }
                    }}
                    type="number"
                />,
                <div key={`rows.${publishersType}Rows[${index}].contactInformation`} className="contact-information">
                    {(() => {
                        const foundContact = contacts?.data?.find(
                            (contact) => contact?.ipId === targetPublisher.topMemberIpId
                        )
                        return foundContact ? <ContactDetailsBlock contact={foundContact} /> : null
                    })()}
                </div>,
            ],
            subTables: isAdminRows
                ? [
                      {
                          headerRow: {
                              row: [
                                  { text: 'Name' },
                                  { text: 'P Number' },
                                  { text: 'IPI' },
                                  { text: 'Represented Writers' },
                              ],
                          },
                          rows: [
                              {
                                  removeCellIdentifier: publisherModel.ipId ?? '',
                                  removable: false,
                                  cells: [
                                      tableCellSanitizer(publisherModel.publisherName),
                                      tableCellSanitizer(publisherModel.hfaPublisherNumber),
                                      tableCellSanitizer(publisherModel.ipiNumber),
                                      <CellDisplayList key={publisherModel.ipId} items={publisherModel.writers} />,
                                  ],
                              },
                          ],
                      },
                  ]
                : undefined,
        }
    })
}
